import { createContext, useContext } from 'react';
import type { AuthError, Session, User } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

export type Profile = Database['public']['Tables']['users']['Row'];
export type Organization = Database['public']['Tables']['organizations']['Row'];

export interface SignUpData {
  first_name: string;
  last_name: string;
  organization_name?: string;
  organization_id?: string;
  role?: 'admin' | 'partner' | 'senior' | 'junior' | 'assistant';
}

export interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  organization: Organization | null;
  session: Session | null;
  loading: boolean;
  isProfileLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, userData: SignUpData) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useUser() {
  return useAuth().user;
}

export function useProfile() {
  return useAuth().profile;
}

export function useOrganization() {
  return useAuth().organization;
}

export function useIsAuthenticated() {
  const { user, loading } = useAuth();
  return { isAuthenticated: Boolean(user), loading };
}
